import { useEffect, useMemo, useState } from "react";
import ui from "../components/ui.module.css";
import { toActivity } from "../data/activityAdapter";
import { redeemTeacherPreview } from "../data/teacherPreviewRepository";
import Activity from "./Activity";
import Checking from "./Checking";

// What a used launch code becomes in the address bar (see App).
export const PREVIEW_OPENED = "opened";

// A launch code can be redeemed only once, so each code is sent once however
// often the effect below runs (React runs it twice in development).
const redemptions = new Map();
const redeemOnce = (code) => {
  if (!redemptions.has(code)) redemptions.set(code, redeemTeacherPreview(code));
  return redemptions.get(code);
};

const Message = ({ title, children, action, alert = false }) => (
  <main className={`${ui.frame} ${ui.center}`}>
    <div className={`${ui.card} ${ui.stack}`} role={alert ? "alert" : undefined}>
      <h1>{title}</h1>
      <p>{children}</p>
      {action}
    </div>
  </main>
);

const TeacherPreview = ({ code }) => {
  const [state, setState] = useState(() =>
    code === PREVIEW_OPENED ? { phase: "closed" } : { phase: "loading" }
  );

  useEffect(() => {
    if (code === PREVIEW_OPENED) return undefined;
    let active = true;
    redeemOnce(code).then(
      (record) => active && setState({ phase: "activity", activity: toActivity(record) }),
      () => active && setState({ phase: "error" })
    );
    return () => {
      active = false;
    };
  }, [code]);

  // Responses live only in this component's memory; nothing is sent anywhere.
  const session = useMemo(() => {
    if (state.phase !== "activity") return null;
    const responses = state.responses || {};
    return {
      activity: state.activity,
      attempt: { id: "teacher-preview", responses },
      stepIndex: state.stepIndex || 0,
      saving: false,
      retry: null,
      completeStep: (response) => {
        setState((current) => {
          const stepIndex = current.stepIndex || 0;
          const step = current.activity.steps[stepIndex];
          const nextResponses = { ...(current.responses || {}), [step.id]: response };
          const next = stepIndex + 1;
          return next >= current.activity.steps.length
            ? { phase: "complete", activity: current.activity }
            : { ...current, responses: nextResponses, stepIndex: next };
        });
      },
      saveProgress: (response) => {
        setState((current) => {
          const step = current.activity.steps[current.stepIndex || 0];
          return {
            ...current,
            responses: { ...(current.responses || {}), [step.id]: response },
          };
        });
      },
      unavailable: () => setState({ phase: "error" }),
    };
  }, [state]);

  if (state.phase === "loading") return <Checking />;
  if (state.phase === "complete") {
    return (
      <Message
        title="Preview finished"
        action={
          <button
            type="button"
            className={`${ui.button} ${ui.secondary}`}
            onClick={() => setState({ phase: "activity", activity: state.activity })}
          >
            Preview again
          </button>
        }
      >
        This is where learners are asked to look at their teacher. No learner work was saved.
      </Message>
    );
  }
  if (state.phase === "closed") {
    return (
      <Message title="Preview closed">
        Open the preview again from the lesson.
      </Message>
    );
  }
  if (state.phase === "error") {
    return (
      <Message title="Preview unavailable" alert>
        Close this preview and try again from the lesson.
      </Message>
    );
  }
  return (
    <>
      <aside className={ui.previewBanner}>Teacher preview · nothing is saved</aside>
      <Activity session={session} />
    </>
  );
};

export default TeacherPreview;
